import { useState, useRef, useEffect } from 'react';
import { classNames } from '~/utils/classNames';
import { themeStore, setTheme, type Theme } from '~/lib/stores/theme';
import { useStore } from '@nanostores/react';
import { Sun, Moon } from 'lucide-react';

const themes: { code: Theme; name: string; displayName: string; icon: React.ReactNode }[] = [
  { code: 'light', name: 'Light', displayName: 'Light', icon: <Sun className="w-4 h-4 text-current" /> },
  { code: 'dark', name: 'Dark', displayName: 'Dark', icon: <Moon className="w-4 h-4 text-current" /> },
];

export function ThemeSwitcher() {
  const currentTheme = useStore(themeStore);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const currentThemeObj = themes.find((theme) => theme.code === currentTheme) || themes[1]; // default dark

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const handleThemeChange = (themeCode: Theme) => {
    setTheme(themeCode);
    setIsOpen(false);
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1.5 px-3 h-[35px] rounded-md bg-dualite-background-button text-dualite-text-primary hover:bg-dualite-background-button-hover transition-colors duration-150"
        aria-label="Change theme"
      >
        <span className="w-[14px] h-[14px] flex items-center justify-center">{currentThemeObj.icon}</span>
        {/* <span className="text-xs font-medium capitalize">{currentThemeObj.name}</span> */}
      </button>

      {isOpen && (
        <div className="absolute top-full right-0 mt-2 w-48 bg-dualite-background-tertiary border border-dualite-border-primary rounded-2xl shadow-xl z-50 overflow-hidden">
          {themes.map((theme) => (
            <button
              key={theme.code}
              onClick={() => handleThemeChange(theme.code)}
              className={classNames(
                'w-full flex items-center gap-2 px-4 py-3 text-left transition-colors',
                'hover:bg-dualite-background-button-hover text-dualite-text-primary',
                currentTheme === theme.code
                  ? 'bg-dualite-background-button-hover'
                  : 'hover:bg-dualite-background-button-hover',
              )}
            >
              <div className="flex items-center justify-between w-full">
                <div className="flex items-center gap-2">
                   {theme.icon}
                   <span className="text-sm font-medium">{theme.displayName}</span>
                </div>
                {currentTheme === theme.code && <div className="i-ph:check-bold w-4 h-4 text-dualite-text-primary" />}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
